import React, { useCallback, useMemo, useState } from 'react';
import { subDays } from 'date-fns';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import { useContentPerformanceSummary } from '../hooks/useContentPerformanceSummary.ts';

const i18n = defineMessages({
	title: {
		id: 'performance-insights.content-metrics.overall-content-performance.title.non-final',
		defaultMessage: 'Overall content performance',
		description:
			'Title for the overall content performance section on the Content metrics tab of the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	subtitle: {
		id: 'performance-insights.content-metrics.overall-content-performance.subtitle.non-final',
		defaultMessage: 'Status based on load time across all pages and live docs',
		description:
			'Subtitle for the overall content performance section describing how performance is measured. Displayed on the Content metrics tab of the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	slow: {
		id: 'performance-insights.content-metrics.overall-content-performance.legend-slow.non-final',
		defaultMessage: 'Slow (> 4s)',
		description:
			'Legend label for the slow performance category in the overall content performance progress bar. Displayed on the Content metrics tab of the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	moderate: {
		id: 'performance-insights.content-metrics.overall-content-performance.legend-moderate.non-final',
		defaultMessage: 'Moderate (3.7-4s)',
		description:
			'Legend label for the moderate performance category in the overall content performance progress bar. Displayed on the Content metrics tab of the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	fast: {
		id: 'performance-insights.content-metrics.overall-content-performance.legend-fast.non-final',
		defaultMessage: 'Fast (< 3.7s)',
		description:
			'Legend label for the fast performance category in the overall content performance progress bar. Displayed on the Content metrics tab of the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	tooltipSlow: {
		id: 'performance-insights.content-metrics.overall-content-performance.tooltip-slow.non-final',
		defaultMessage: '{count} / {total} boards with slow load time',
		description:
			'Tooltip message shown when hovering over the slow segment of the overall content performance progress bar. Displays the count and total number of boards with slow load time. Displayed on the Content metrics tab of the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	tooltipModerate: {
		id: 'performance-insights.content-metrics.overall-content-performance.tooltip-moderate.non-final',
		defaultMessage: '{count} / {total} boards with moderate load time',
		description:
			'Tooltip message shown when hovering over the moderate segment of the overall content performance progress bar. Displays the count and total number of boards with moderate load time. Displayed on the Content metrics tab of the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	tooltipFast: {
		id: 'performance-insights.content-metrics.overall-content-performance.tooltip-fast.non-final',
		defaultMessage: '{count} / {total} boards with fast load time',
		description:
			'Tooltip message shown when hovering over the fast segment of the overall content performance progress bar. Displays the count and total number of boards with fast load time. Displayed on the Content metrics tab of the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
	percentageLabel: {
		id: 'performance-insights.content-metrics.overall-content-performance.percentage-label.non-final',
		defaultMessage: '{percent}%',
		description:
			'Percentage label displayed on each segment of the overall content performance progress bar. Displayed on the Content metrics tab of the Performance analytics page. The page is only viewable to admins. The page is part of the Confluence Administration section of Confluence.',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
		padding: token('space.200'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
	},
	progressBarContainer: {
		width: '100%',
		height: '8px',
		borderRadius: token('radius.xsmall'),
		overflow: 'visible',
		position: 'relative',
	},
	segment: {
		height: '100%',
		position: 'relative',
		cursor: 'pointer',
	},
	percentageLabel: {
		position: 'absolute',
		top: token('space.200'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
	},
	tooltip: {
		position: 'absolute',
		left: '50%',
		top: token('space.negative.400'),
		transform: 'translateX(-50%) translateY(-25%)',
		marginBottom: token('space.100'),
		padding: token('space.100'),
		backgroundColor: token('color.background.neutral.bold'),
		color: token('color.text.inverse'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
		whiteSpace: 'nowrap',
		pointerEvents: 'none',
		zIndex: 1,
	},
	legend: {
		display: 'flex',
		marginBlockStart: token('space.500'),
		gap: token('space.200'),
	},
	legendItem: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text.subtle'),
	},
	legendColorBox: {
		width: '12px',
		height: '12px',
		borderRadius: token('radius.xsmall'),
	},
	subtitleContainer: {
		marginBlockStart: token('space.150'),
	},
	titleContainer: {
		marginBottom: token('space.200'),
	},
});

const OverallContentPerformanceInternal = () => {
	const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);
	const { startTimestamp, endTimestamp } = useMemo(() => {
		const now = new Date();

		return {
			startTimestamp: subDays(now, 7).toISOString(),
			endTimestamp: now.toISOString(),
		};
	}, []);

	const { data, loading, error } = useContentPerformanceSummary({
		startTimestamp,
		endTimestamp,
	});

	const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLElement>) => {
		setHoveredSegment(e.currentTarget.dataset.segmentKey ?? null);
	}, []);

	const handleMouseLeave = useCallback(() => {
		setHoveredSegment(null);
	}, []);

	if (error) {
		return <ErrorDisplay error={error} />;
	}

	// @todo: Implement proper handling for loading and empty data states
	if (loading || !data) {
		return null;
	}

	const { fast, moderate, slow, total } = data;

	const segments = [
		{
			key: 'fast',
			percent: fast.percentage,
			count: fast.count,
			color: token('color.background.success.bold'),
			label: i18n.fast,
			tooltipMessage: i18n.tooltipFast,
		},
		{
			key: 'moderate',
			percent: moderate.percentage,
			count: moderate.count,
			color: token('color.background.warning.bold'),
			label: i18n.moderate,
			tooltipMessage: i18n.tooltipModerate,
		},
		{
			key: 'slow',
			percent: slow.percentage,
			count: slow.count,
			color: token('color.background.danger.bold'),
			label: i18n.slow,
			tooltipMessage: i18n.tooltipSlow,
		},
	];

	return (
		<Box xcss={styles.container}>
			<Box xcss={styles.titleContainer}>
				<Heading as="h2" size="xsmall">
					<FormattedMessage {...i18n.title} />
				</Heading>
			</Box>

			<Flex xcss={styles.progressBarContainer}>
				{segments.map((segment) => (
					<Box
						key={segment.key}
						xcss={styles.segment}
						style={{
							width: `${segment.percent}%`,
							backgroundColor: segment.color,
						}}
						testId={`progress-bar-segment-${segment.key}`}
						data-segment-key={segment.key}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
					>
						<Box xcss={styles.percentageLabel}>
							<FormattedMessage
								{...i18n.percentageLabel}
								values={{ percent: Math.round(segment.percent) }}
							/>
						</Box>
						{hoveredSegment === segment.key && (
							<Box xcss={styles.tooltip} testId={`progress-bar-segment-${segment.key}-tooltip`}>
								<FormattedMessage
									{...segment.tooltipMessage}
									values={{ count: segment.count, total }}
								/>
							</Box>
						)}
					</Box>
				))}
			</Flex>

			<Flex xcss={styles.legend}>
				{segments.map((segment) => (
					<Flex key={segment.key} xcss={styles.legendItem}>
						<Box xcss={styles.legendColorBox} style={{ backgroundColor: segment.color }} />
						<Box>
							<FormattedMessage {...segment.label} />
						</Box>
					</Flex>
				))}
			</Flex>

			<Box xcss={styles.subtitleContainer}>
				<Text size="small" color="color.text.subtlest">
					<FormattedMessage {...i18n.subtitle} />
				</Text>
			</Box>
		</Box>
	);
};

export const OverallContentPerformance = withErrorBoundary({
	attribution: Attribution.PERFORMANCE_INSIGHTS,
})(OverallContentPerformanceInternal);
